import { Controller } from "@hotwired/stimulus"
import Papa from "papaparse"
import { figureColumnsIn, parseCsvPreview, prepareCsvPreview, readCsvPreview } from "../lib/csv_preview"
import { emptyStatePlaceholder } from "./tabulator_helpers"

// Connects to data-controller="csv-viewer"
// Uses global window.Tabulator (loaded in application.js)
export default class extends Controller {
  static targets = ["container", "tabs", "table", "warning", "cellModal", "modalTitle", "modalRow", "modalCol", "modalContent"]
  static values = {
    sheets: Array,  // Array of { index, name, url, rows, columns }
    activeSheet: { type: Number, default: 0 },
    maxColumns: { type: Number, default: 100 },
    maxRows: { type: Number, default: 10000 },
    maxCells: { type: Number, default: 500000 },
    maxBytes: { type: Number, default: 16 * 1024 * 1024 }
  }

  tabulator = null
  sheetData = new Map() // At most two sheets, within one preview cell budget
  resizeTimeout = null
  truncatedColumns = false
  boundHandleResize = this.handleResize.bind(this)

  connect() {
    this.connected = true
    if (this.sheetsValue.length > 0) {
      this.loadSheet(this.activeSheetValue)
    }

    // Handle window resize
    window.addEventListener('resize', this.boundHandleResize)
  }

  disconnect() {
    this.connected = false
    this.sheetRequest?.abort()
    this.sheetRequest = null
    this.sheetData.clear()
    clearTimeout(this.resizeTimeout)
    this.resizeTimeout = null
    console.log('CSV viewer disconnected')
    if (this.tabulator) {
      this.tabulator.destroy()
      this.tabulator = null
    }
    window.removeEventListener('resize', this.boundHandleResize)
  }

  handleResize() {
    clearTimeout(this.resizeTimeout)
    this.resizeTimeout = setTimeout(() => {
      if (this.tabulator) {
        this.tabulator.redraw(true)
      }
    }, 100)
  }

  switchSheet(event) {
    const sheetIndex = parseInt(event.currentTarget.dataset.sheetIndex, 10)
    if (sheetIndex !== this.activeSheetValue) {
      this.activeSheetValue = sheetIndex
      this.loadSheet(sheetIndex)
      this.updateTabsUI()
    }
  }

  updateTabsUI() {
    if (!this.hasTabsTarget) return

    this.tabsTarget.querySelectorAll('[data-sheet-index]').forEach(tab => {
      const index = parseInt(tab.dataset.sheetIndex, 10)
      const active = index === this.activeSheetValue
      tab.classList.toggle('tab-active', active)
      tab.setAttribute('aria-selected', String(active))
    })
  }

  async loadSheet(sheetIndex) {
    const sheet = this.sheetsValue[sheetIndex]
    if (!sheet) {
      console.error('Sheet not found:', sheetIndex)
      return
    }
    this.sheetRequest?.abort()
    const request = new AbortController()
    this.sheetRequest = request

    // Retire the old table before replacing its DOM or loading another sheet.
    this.destroyTable()
    this.showLoading()

    if (this.sheetData.has(sheetIndex)) {
      const preview = this.sheetData.get(sheetIndex)
      this.sheetData.delete(sheetIndex)
      this.sheetData.set(sheetIndex, preview)
      this.renderPreview(preview, sheet)
      return
    }

    try {
      const response = await fetch(sheet.url, { signal: request.signal })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)

      const csvText = await readCsvPreview(response, this.maxBytesValue, request.signal)
      if (!this.connected || this.sheetRequest !== request) return

      // Keep identifiers as strings. Parsing stops after a bounded row prefix;
      // the original remains available through the existing download action.
      const parsed = await parseCsvPreview(csvText, Papa.parse, {
        maxRows: this.maxRowsValue, signal: request.signal
      })
      const preview = await prepareCsvPreview(parsed.data, {
        maxColumns: this.maxColumnsValue, maxRows: this.maxRowsValue,
        maxCells: this.maxCellsValue, signal: request.signal
      })
      preview.limitedRows ||= Boolean(parsed.meta?.truncated)
      preview.parseWarningCount = parsed.errorCount ?? parsed.errors?.length ?? 0
      if (!this.connected || this.sheetRequest !== request) return
      this.cachePreview(sheetIndex, preview)
      this.renderPreview(preview, sheet)
    } catch (error) {
      if (error.name === 'AbortError' || !this.connected || this.sheetRequest !== request) return
      request.abort()
      console.error('Failed to load CSV:', error)
      this.showError(error.message)
    }
  }

  cachePreview(sheetIndex, preview) {
    this.sheetData.delete(sheetIndex)
    this.sheetData.set(sheetIndex, preview)
    let cells = [...this.sheetData.values()].reduce((sum, entry) => sum + entry.cellCount, 0)
    while (this.sheetData.size > 1 && (this.sheetData.size > 2 || cells > this.maxCellsValue)) {
      const oldest = this.sheetData.keys().next().value
      cells -= this.sheetData.get(oldest).cellCount
      this.sheetData.delete(oldest)
    }
  }

  renderPreview(preview, sheet) {
    this.renderTable(preview.data, sheet, preview.truncated, preview.originalColumnCount,
      preview.columnsToKeep, preview.headerlessDetected, preview.limitedRows, preview.parseWarningCount)
  }

  destroyTable() {
    if (!this.tabulator) return
    this.tabulator.destroy()
    this.tabulator = null
  }

  clearWarnings() {
    if (!this.hasWarningTarget) return
    this.warningTarget.innerHTML = ''
    this.warningTarget.classList.add('hidden')
  }

  showLoading() {
    this.clearWarnings()
    if (this.hasTableTarget) {
      this.tableTarget.innerHTML = `
        <div class="flex items-center justify-center h-64" role="status">
          <span class="loading loading-spinner loading-lg text-brand-accent" aria-hidden="true"></span>
          <span class="sr-only">Loading spreadsheet preview</span>
        </div>
      `
    }
  }

  showError(message) {
    this.clearWarnings()
    if (this.hasTableTarget) {
      this.tableTarget.innerHTML = `
        <div class="flex flex-col items-center justify-center h-64 text-center" role="alert">
          <i class="ti ti-file-alert text-6xl text-error mb-4"></i>
          <p class="text-error font-medium">Failed to load spreadsheet</p>
          <p class="text-sm text-ink-soft mt-1">${this.escapeHtml(message)}</p>
        </div>
      `
    }
  }

  renderTable(data, sheet, truncated = false, originalColumnCount = 0, columnsToKeep = [], headerlessDetected = false, limitedRows = false, parseWarningCount = 0) {
    if (!this.hasTableTarget) return

    this.destroyTable()
    this.tableTarget.innerHTML = ''

    const columnKeys = (columnsToKeep.length > 0 ? columnsToKeep : Object.keys(data?.[0] || {})).filter(k => k !== '_csvRow')
    data ||= []
    // Show viewer warnings when we auto-detect a headerless file and/or truncate columns for performance.
    const warnings = []
    if (parseWarningCount) warnings.push('Some rows have CSV parsing warnings. Download the original to check the complete data.')
    if (limitedRows) warnings.push(`Showing the first ${data.length.toLocaleString()} rows (the preview limit). Download the original for every row.`)
    if (data.length && headerlessDetected) warnings.push('No header row found, so the columns are numbered (Column 1, Column 2, …).')
    if (truncated) warnings.push(`Showing ${columnKeys.length} of ${originalColumnCount.toLocaleString()} columns; empty columns are hidden. Download the original for every column.`)

    if (this.hasWarningTarget) {
      this.warningTarget.setAttribute('role', 'status')
      this.warningTarget.setAttribute('aria-live', 'polite')
    }
    if (warnings.length > 0 && this.hasWarningTarget) {
      // One quiet line per note above the grid: what the preview leaves out is
      // worth knowing, not an alarm.
      this.warningTarget.innerHTML = warnings
        .map((message) => `<p class="csv-note__line"><i class="ti ti-info-circle" aria-hidden="true"></i><span>${message}</span></p>`)
        .join('')
      this.warningTarget.classList.remove('hidden')
    } else if (this.hasWarningTarget) {
      this.warningTarget.innerHTML = ''
      this.warningTarget.classList.add('hidden')
    }

    if (!data || data.length === 0) {
      this.tableTarget.innerHTML = `
        <div class="section-empty">
          <div class="section-empty__copy">
            <p class="section-empty__title">No data in this sheet</p>
          </div>
        </div>
      `
      return
    }

    // Get column definitions from the first row's keys
    const columnWidths = this.buildColumnWidthMap(data, columnKeys)
    const figureColumns = figureColumnsIn(data, columnKeys)
    const columns = [
      {
        title: "#",
        field: "_csvRow",
        frozen: true,
        headerSort: false,
        resizable: false,
        width: 50,
        hozAlign: "center",
        cssClass: "csv-row-index-cell",
      },
      ...columnKeys.map(key => ({
        title: key,
        titleFormatter: cell => this.escapeHtml(cell.getValue()),
        field: key,
        formatter: "plaintext",
        headerFilter: "input",
        headerFilterPlaceholder: "Filter",
        // Figures sit right, so their digits line up down the column.
        hozAlign: figureColumns.has(key) ? "right" : "left",
        headerHozAlign: figureColumns.has(key) ? "right" : "left",
        cssClass: figureColumns.has(key) ? "csv-figure" : undefined,
        resizable: true,
        minWidth: 100,
        width: columnWidths[key],
        tooltip: (_event, cell) => this.escapeHtml(cell.getValue() ?? ""),
        headerTooltip: () => this.escapeHtml(key),
        headerWordWrap: false,
        variableHeight: true,
        // Tabulator 6 takes cellClick per column, not as a table option.
        cellClick: (e, cell) => this.handleCellClick(e, cell)
      }))
    ]

    // Create Tabulator instance
    this.tabulator = new window.Tabulator(this.tableTarget, {
      data: data,
      columns: columns,
      // Widths are already sampled and bounded above. fitDataFill remeasures
      // every column on each layout, including offscreen virtual columns.
      // fitData restricts forced width refreshes to the horizontal window.
      layout: "fitData",
      height: "100%",
      maxHeight: "100%",
      renderVertical: "virtual",
      renderVerticalBuffer: 300,
      nestedFieldSeparator: false, // Spreadsheet headers are literal field names
      pagination: data.length > 100, // Paginate if > 100 rows
      paginationSize: 50,
      paginationSizeSelector: [25, 50, 100, 200],
      movableColumns: true,
      placeholder: emptyStatePlaceholder("No rows"),
      headerSortClickElement: "icon",
      renderHorizontal: "virtual" // Virtual horizontal scrolling for many columns
    })

    // Log table info
    console.log(`CSV loaded: ${data.length} rows, ${columns.length} columns${truncated ? ` (truncated from ${originalColumnCount})` : ''}${headerlessDetected ? ' (headerless detected)' : ''}`)
  }

  handleCellClick(e, cell) {
    const field = cell.getField()
    if (field === "_csvRow") return // Ignore index column

    const value = cell.getValue()
    if (value === null || value === undefined || String(value).trim() === "") return
    // Only a cell cut off at its edge opens; a whole value copies from the hover pill.
    const element = cell.getElement()
    if (element.scrollWidth <= element.clientWidth && !String(value).includes("\n")) return

    // Get column title and row index
    const columnTitle = cell.getColumn().getDefinition().title
    const rowIndex = cell.getRow().getData()._csvRow || cell.getRow().getIndex()

    // Format content: if it looks like JSON, pretty print it!
    let displayContent = String(value)
    let isJson = false
    try {
      // Check if it looks like JSON before trying to parse
      const trimmed = displayContent.trim()
      if ((trimmed.startsWith('{') && trimmed.endsWith('}')) ||
          (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
        const parsed = JSON.parse(trimmed)
        displayContent = JSON.stringify(parsed, null, 2)
        isJson = true
      }
    } catch (err) {
      // Not JSON, keep original string
    }

    // Populate modal targets
    if (this.hasModalTitleTarget) this.modalTitleTarget.textContent = columnTitle
    if (this.hasModalRowTarget) this.modalRowTarget.textContent = rowIndex
    if (this.hasModalColTarget) this.modalColTarget.textContent = columnTitle

    if (this.hasModalContentTarget) {
      if (isJson) {
        this.modalContentTarget.innerHTML = `<pre class="csv-cell__json select-all">${this.escapeHtml(displayContent)}</pre>`
      } else {
        this.modalContentTarget.textContent = displayContent
      }
    }

    // Open DaisyUI modal
    if (this.hasCellModalTarget) {
      this.cellModalTarget.showModal()
    }
  }

  escapeHtml(string) {
    return String(string)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;")
  }

  copyCellContent() {
    if (!this.hasModalContentTarget) return
    const textToCopy = this.modalContentTarget.textContent
    navigator.clipboard.writeText(textToCopy).then(() => {
      // Show a temporary "Copied!" state on the button
      const copyBtn = this.element.querySelector('[data-action="click->csv-viewer#copyCellContent"]')
      if (copyBtn) {
        copyBtn.textContent = 'Copied'
        setTimeout(() => { copyBtn.textContent = 'Copy' }, 1500)
      }
    }).catch(err => {
      console.error('Failed to copy text: ', err)
    })
  }

  buildColumnWidthMap(data, columnKeys) {
    const maxRowsToSample = 500
    const minWidth = 100
    const maxWidth = 520
    const paddingWidth = 36
    const averageCharacterWidth = 8
    // The head also holds the sort arrow, so a title needs a little more room
    // than a value of the same length.
    const sortArrowWidth = 22
    const widthMap = Object.create(null)
    const rowsToSample = data.slice(0, maxRowsToSample)

    columnKeys.forEach((key) => {
      let maxLength = 0

      rowsToSample.forEach((row) => {
        const rawValue = row?.[key]
        if (rawValue === null || rawValue === undefined) return
        const valueLength = String(rawValue).length
        if (valueLength > maxLength) {
          maxLength = valueLength
        }
      })

      const valueWidth = (maxLength * averageCharacterWidth) + paddingWidth
      const titleWidth = (key.length * averageCharacterWidth) + paddingWidth + sortArrowWidth
      widthMap[key] = Math.max(minWidth, Math.min(maxWidth, Math.max(valueWidth, titleWidth)))
    })

    return widthMap
  }
}
